import type {
  InitiativeDetail,
  InitiativeStats,
  InitiativeSummary,
  PaginatedResponse,
  TransitionResponse,
} from '@/domain/types'
import type { StageCode } from '@/domain/funnel'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'
import { useApiAction, type UseApiActionResult } from '@/api/useApiAction'

/**
 * The initiative endpoints.
 */

export interface InitiativeListQuery {
  page?: number
  pageSize?: number
  /** `closed` is both closed stages: Close is one stage with an outcome. */
  stage?: StageCode | 'closed'
  ownerId?: string
  q?: string
  sort?: InitiativeSort
}

export type InitiativeSort = 'recent' | 'funnel'

export function useInitiativeList(
  query: InitiativeListQuery,
  refreshKey = 0,
): QueryState<PaginatedResponse<InitiativeSummary>> {
  return useApiQuery<PaginatedResponse<InitiativeSummary>>(
    buildInitiativeListPath(query),
    refreshKey,
  )
}

export function buildInitiativeListPath(q: InitiativeListQuery): string {
  const params = new URLSearchParams()
  if (q.page !== undefined) params.set('page', String(q.page))
  if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize))
  if (q.stage !== undefined) params.set('stage', q.stage)
  if (q.ownerId !== undefined) params.set('ownerId', q.ownerId)
  if (q.q) params.set('q', q.q)
  if (q.sort !== undefined) params.set('sort', q.sort)
  const qs = params.toString()
  return qs ? `/api/initiatives?${qs}` : '/api/initiatives'
}

/**
 * The fat detail: every field the page draws, in one request. Includes
 * `readiness` straight from the API's own `assessReadiness`, which is why the
 * SPA holds no copy of the gate rules.
 */
export function useInitiativeDetail(id: string, refreshKey = 0): QueryState<InitiativeDetail> {
  return useApiQuery<InitiativeDetail>(`/api/initiatives/${id}`, refreshKey)
}

/** A Blob Storage URL a video element streams an evidence video from; it lasts 50 minutes. */
export function useEvidenceVideo(
  initiativeId: string,
  resultId: string,
): QueryState<{ url: string; expiresAt: string }> {
  return useApiQuery<{ url: string; expiresAt: string }>(
    `/api/initiatives/${initiativeId}/results/${resultId}/video`,
  )
}

/** The funnel board's numbers, plus the count of ideas nobody has promoted. */
export function useKeystoneStats(refreshKey = 0): QueryState<InitiativeStats> {
  return useApiQuery<InitiativeStats>('/api/initiatives/stats', refreshKey)
}

export function useTransition(): UseApiActionResult<TransitionResponse> {
  return useApiAction<TransitionResponse>()
}

/**
 * Patch, evidence add/delete, allocation put/delete and the Admin delete all go
 * through one action.
 */
export function useInitiativeEdit(): UseApiActionResult<unknown> {
  return useApiAction<unknown>()
}

/**
 * The most recently touched initiatives, for the home page's short list.
 *
 * The old hook pulled the widest page the contract allowed and sorted client
 * side, because the endpoint ordered by id and took no sort param — so past
 * 100 initiatives an old one edited this morning fell outside the window and
 * missed the list. `sort=recent` is `modified_at DESC` on the server, which
 * makes that whole workaround unnecessary.
 */
export function useRecentInitiatives(
  limit: number,
  refreshKey = 0,
): QueryState<PaginatedResponse<InitiativeSummary>> {
  return useInitiativeList({ page: 1, pageSize: limit, sort: 'recent' }, refreshKey)
}
